import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, RefreshCw, Bell, ShieldCheck, Filter, X,
  FileSpreadsheet, User, KeyRound, LogIn, Building2, Trash2, FileSignature,
} from "lucide-react";

// Single lookup table for every event we emit (both audit + notification kinds).
const ACTION_META = {
  "auth.login":              { label: "Sign in",                 icon: LogIn,           tone: "text-zinc-700" },
  "user.approve":            { label: "User approved",           icon: ShieldCheck,     tone: "text-emerald-700" },
  "user.deny":               { label: "User denied",             icon: ShieldCheck,     tone: "text-red-700" },
  "user.delete":             { label: "User deleted",            icon: Trash2,          tone: "text-red-700" },
  "user.bulk_delete":        { label: "Users bulk-deleted",      icon: Trash2,          tone: "text-red-700" },
  "password_reset.approve":  { label: "Password reset approved", icon: KeyRound,        tone: "text-emerald-700" },
  "password_reset.deny":     { label: "Password reset denied",   icon: KeyRound,        tone: "text-red-700" },
  "bu.delete":               { label: "BU deleted",              icon: Building2,       tone: "text-red-700" },
  "template.submit":         { label: "Template submitted",      icon: FileSpreadsheet, tone: "text-amber-700" },
  "template.owner_approve":  { label: "Template owner-approved", icon: FileSpreadsheet, tone: "text-violet-700" },
  "template.super_approve":  { label: "Template signed off",     icon: FileSpreadsheet, tone: "text-emerald-700" },
  "template.delete":         { label: "Template deleted",        icon: Trash2,          tone: "text-red-700" },
  "template.bulk_delete":    { label: "Templates bulk-deleted",  icon: Trash2,          tone: "text-red-700" },
  "template.unlock":         { label: "Template unlocked",       icon: FileSpreadsheet, tone: "text-amber-700" },
  "review.manager_submit":   { label: "Review · manager sign-off",  icon: FileSpreadsheet, tone: "text-amber-700" },
  "review.employee_submit":  { label: "Review · employee sign-off", icon: FileSpreadsheet, tone: "text-blue-700" },
  "review.final_submit":     { label: "Review · final sign-off",    icon: FileSpreadsheet, tone: "text-emerald-700" },
  "review.delete":           { label: "Review deleted",             icon: Trash2,          tone: "text-red-700" },
  "policy.upload":           { label: "Policy uploaded",         icon: FileSignature,   tone: "text-blue-700" },
  "policy.slots_update":     { label: "Policy slots set",        icon: FileSignature,   tone: "text-zinc-700" },
  "policy.sign":             { label: "Policy signed",           icon: FileSignature,   tone: "text-emerald-700" },
  "policy.delete":           { label: "Policy deleted",          icon: Trash2,          tone: "text-red-700" },
};

/**
 * Unified Activity feed.
 *  - Super Admin: sees all audit events (org-wide) + their own inbox notifications, merged.
 *  - Everyone else: sees only their own audit events + their own notifications.
 * Both streams share the same table + filters.
 */
export default function Activity() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState(""); // "", "audit", "inbox"
  const [userFilter, setUserFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (actionFilter) params.set("action", actionFilter);
      if (userFilter && (isSuper || user?.role === "bu_owner" || user?.role === "bu_manager")) params.set("actor_id", userFilter);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      params.set("limit", "500");
      const canFilterByUser = isSuper || user?.role === "bu_owner" || user?.role === "bu_manager";
      const [audit, notifs, u] = await Promise.all([
        api.get(`/audit?${params.toString()}`).catch(() => ({ data: [] })),
        api.get("/notifications").catch(() => ({ data: [] })),
        canFilterByUser ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      // Normalise both streams into a single row shape.
      const auditRows = (audit.data || []).map((e) => ({
        id: `a-${e.id}`,
        source: "audit",
        when: e.created_at,
        actor: e.actor_email || "—",
        actor_role: e.actor_role,
        action: e.action,
        summary: e.summary,
        raw: e,
      }));
      const inboxRows = (notifs.data || []).map((n) => ({
        id: `n-${n.id}`,
        source: "inbox",
        when: n.created_at,
        actor: "System", // notifications are addressed *to* you; not "from" anyone specific
        action: "inbox",
        summary: n.subject + (n.body ? ` — ${n.body}` : ""),
        read: n.read,
        raw: n,
      }));
      const merged = [...auditRows, ...inboxRows].sort(
        (a, b) => (b.when || "").localeCompare(a.when || "")
      );
      setItems(merged);
      setUsers(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (sourceFilter && r.source !== sourceFilter) return false;
      if (unreadOnly && !(r.source === "inbox" && r.read === false)) return false;
      // Client-side date filter also covers the inbox rows (server-side only reaches audit).
      if (fromDate && r.when && r.when < fromDate) return false;
      if (toDate) {
        const bound = toDate.length === 10 ? `${toDate}T23:59:59.999Z` : toDate;
        if (r.when && r.when > bound) return false;
      }
      if (search) {
        const s = search.toLowerCase();
        if (!(`${r.summary} ${r.actor} ${r.action}`.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [items, sourceFilter, unreadOnly, fromDate, toDate, search]);

  // Distinct actions present — populates the action dropdown.
  const actions = useMemo(() => Array.from(new Set(items.map((i) => i.action))).sort(), [items]);
  const hasFilter = search || actionFilter || sourceFilter || userFilter || fromDate || toDate || unreadOnly;

  const markInboxRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((rows) => rows.map((r) => (r.id === `n-${id}` ? { ...r, read: true } : r)));
    } catch { /* ignore */ }
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/mark-all-read"); load(); toast.success("All notifications marked read"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const inboxUnread = items.filter((r) => r.source === "inbox" && r.read === false).length;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Timeline</div>
          <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isSuper ? "Every system event + your personal notifications." : "Your actions + your personal notifications."}
          </p>
        </div>
        {inboxUnread > 0 && (
          <button onClick={markAllRead} className="text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50 flex items-center gap-1" data-testid="activity-mark-all-read">
            <Bell className="w-3.5 h-3.5" /> Mark all {inboxUnread} notifications read
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <div className="col-span-2 md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Search</label>
          <div className="relative mt-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="activity-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Summary, actor, action…"
              className="w-full border border-zinc-300 pl-7 pr-2 py-1.5 rounded-sm text-xs"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">From</label>
          <input data-testid="activity-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">To</label>
          <input data-testid="activity-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Source</label>
          <select data-testid="activity-source-filter" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs bg-white">
            <option value="">All</option>
            <option value="audit">Audit</option>
            <option value="inbox">Inbox</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Action</label>
          <select data-testid="activity-action-filter" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs bg-white">
            <option value="">All</option>
            {actions.map((a) => (
              <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
            ))}
          </select>
        </div>
        {(isSuper || user?.role === "bu_owner" || user?.role === "bu_manager") && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">User (actor)</label>
            <select data-testid="activity-user-filter" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs bg-white">
              <option value="">
                {isSuper ? "Anyone" : user?.role === "bu_owner" ? "Anyone in my BU" : "Me + my direct reports"}
              </option>
              {(isSuper
                  ? users
                  : user?.role === "bu_owner"
                    ? users.filter((u) => u.business_unit_id === user?.business_unit_id)
                    : users.filter((u) => u.id === user?.id || u.line_manager_id === user?.id)
              ).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
        )}
        <div className="col-span-2 md:col-span-6 flex items-center gap-3 pt-2 border-t border-zinc-100">
          <label className="text-xs flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="activity-unread-only" />
            <span>Unread notifications only</span>
          </label>
          <div className="flex-1" />
          {hasFilter && (
            <button
              data-testid="activity-clear-filters"
              onClick={() => { setSearch(""); setActionFilter(""); setSourceFilter(""); setUserFilter(""); setFromDate(""); setToDate(""); setUnreadOnly(false); setTimeout(load, 0); }}
              className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          <button
            data-testid="activity-apply-btn"
            onClick={load}
            style={{ background: "var(--brand-primary)" }}
            className="text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:opacity-90 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Apply
          </button>
        </div>
      </div>

      {/* Events */}
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3 w-40">When</th>
              <th className="text-left px-5 py-3 w-24">Source</th>
              <th className="text-left px-5 py-3 w-52">Actor / To</th>
              <th className="text-left px-5 py-3 w-56">Action</th>
              <th className="text-left px-5 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-16 text-center text-zinc-400 text-sm">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-16 text-center text-zinc-500 text-sm">No events match this filter.</td></tr>
            ) : filtered.map((r) => {
              const meta = ACTION_META[r.action] || { label: r.source === "inbox" ? "Notification" : r.action, icon: r.source === "inbox" ? Bell : User, tone: "text-zinc-700" };
              const Icon = meta.icon;
              const isUnread = r.source === "inbox" && r.read === false;
              return (
                <tr key={r.id} data-testid={`activity-row-${r.id}`}
                    onClick={() => isUnread && markInboxRead(r.raw.id)}
                    className={`border-t border-zinc-200 ${isUnread ? "bg-blue-50/40 cursor-pointer hover:bg-blue-50/70" : "hover:bg-zinc-50"}`}>
                  <td className="px-5 py-3 text-zinc-600 text-xs tabular-nums">{r.when ? new Date(r.when).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3">
                    {r.source === "audit" ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-zinc-300 text-zinc-600 whitespace-nowrap">Audit</span>
                    ) : (
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border whitespace-nowrap"
                        style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)", background: "color-mix(in srgb, var(--brand-primary) 8%, white)" }}
                      >
                        {isUnread ? "Inbox • unread" : "Inbox"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-sm font-medium">{r.actor}</div>
                    {r.actor_role && <div className="text-[10px] uppercase tracking-wider text-zinc-500">{r.actor_role.replace("_", " ")}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-zinc-700">{r.summary}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && filtered.length >= 500 && (
        <div className="text-xs text-zinc-500 mt-3 flex items-center gap-1">
          <Filter className="w-3 h-3" /> Showing the most recent 500 events. Use the filters to narrow down.
        </div>
      )}
    </div>
  );
}
